import { MeetingLocation, ServiceEventType, TeamEventMeetingType } from '@vendasta/meetings';
export const MAX_BATCH_EVENTS = 5;
import { getTimezone as galaxyGetTimezone, getTimezoneNames as galaxyGetTimezoneNames, isValidTimezone } from '@vendasta/galaxy/utility/timezone';
import abbreviations from '@vvo/tzdb/abbreviations.json';
import { getTimezoneOffset } from 'date-fns-tz';
import { TimeSlot } from './bookme/bookme';
import { MeetingType } from './interface';

function zoneAbbr(timezone: string, date: Date): string {
  const shortName = getIntlTimeZoneName(timezone, date, 'short');
  if (shortName && !/^GMT[+-]/.test(shortName)) {
    return shortName;
  }

  const longName = getIntlTimeZoneName(timezone, date, 'long');
  return (abbreviations as Record<string, string>)[longName] || shortName || '';
}

function getIntlTimeZoneName(timezone: string, date: Date, timeZoneName: 'short' | 'long'): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value || ''
    );
  } catch {
    return '';
  }
}

function timeInZone(timezone: string, date: Date): string {
  return date.toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function getTimezone(): string {
  return galaxyGetTimezone();
}

export function getTimezoneNames(): string[] {
  return galaxyGetTimezoneNames();
}

/**
 * Accepts dropdown selections (canonical name or display label) plus stored values that are
 * valid IANA/Intl zones outside the canonical list (aliases like UTC, Asia/Calcutta, US/Eastern
 * that moment.tz.names() used to include).
 */
export function isAcceptableTimezoneValue(
  value: string,
  options: { display: string; timezone: string }[] | undefined,
): boolean {
  return (
    (options ?? []).some((option) => option.timezone === value || option.display === value) || isValidTimezone(value)
  );
}

export function getZoneFromTimezone(
  timezone: string,
  _timezoneOffset: number,
  date: Date = new Date(),
): string {
  if (!timezone) {
    return '';
  }
  return zoneAbbr(timezone, date);
}

export function getTimezoneOffsetFromTimezone(req: {
  timeZone: string;
  fromStart?: Date;
}): string {
  const date = new Date(req.fromStart || new Date());
  // We're setting hours to noon here because noon is the "median" timeslot a person
  // would reasonably book. This is so the date that a timezone shift occurs on won't accidentally not
  // be shifted to due time on new Date() being set to midnight.
  date.setHours(12);
  const totalMinutes = Math.round(getTimezoneOffset(req.timeZone, date) / 60000);
  const sign = totalMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(totalMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

export function buildTimezoneDisplay(timezone: string): string {
  if (!timezone) {
    return '';
  }
  const now = new Date();
  return `${getZoneFromTimezone(timezone, 0, now)} (${timezone}) - ${timeInZone(timezone, now)}`;
}

export function buildShortTimezoneDisplay(timezone: string): string {
  if (!timezone) {
    return '';
  }
  return `(${getZoneFromTimezone(timezone, 0)} - ${timezone})`;
}

export function runningInCypress(): boolean {
  return (
    typeof window !== 'undefined' &&
    window &&
    !!((window as unknown as Record<string, unknown>) || {}).Cypress
  );
}

export function trimTimeRangeToFromNow(timeSlot: TimeSlot): TimeSlot {
  if (!timeSlot || timeSlot.start.getTime() >= new Date().getTime()) {
    return timeSlot;
  }
  return { ...timeSlot, start: new Date() };
}

/**
 * If bookme-client is being used in a iframe with a different domain
 * localstorage access will fail on Chrome
 * https://www.chromium.org/for-testers/bug-reporting-guidelines/uncaught-securityer\
 * ror-failed-to-read-the-localstorage-property-from-window-access-is-denied-for-thi\
 * s-document
 *
 * this solution is cited here:
 * https://michalzalecki.com/why-using-localStorage-directly-is-a-bad-idea/
 */
export function isLocalStorageAvailable(): boolean {
  try {
    // move to inside the try/catch to avoid the error
    // Failed to read the 'localStorage' property from 'Window': Access is denied for this document.
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    const key = '__some_random_key_just_to_test_localstorage__';
    window.localStorage.setItem(key, key);
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function showDemo(): boolean {
  return (
    typeof location !== 'undefined' &&
    location.pathname.indexOf('dmario-inbound') >= 0
  );
}

/**
 * Parses a phone number value formatted as ${countryCallingCode}-${phoneNumber}-${countryCode}
 * and returns a phoneFields object with countryCode (numeric), nationalNumber, and extension.
 * @param phoneNumberValue - The formatted phone number string
 * @returns PhoneFields object or undefined if phoneNumberValue is empty or invalid
 */
export function parsePhoneNumberToPhoneFields(
  phoneNumberValue: string | null | undefined
):
  | { countryCode: string; nationalNumber: string; extension: string }
  | undefined {
  if (!phoneNumberValue) {
    return undefined;
  }

  // Phone number format is: ${countryCallingCode}-${phoneNumber}-${countryCode}
  const phoneParts = phoneNumberValue.split('-');
  if (phoneParts.length >= 3) {
    const countryCallingCode = phoneParts[0]; // Numeric country calling code (e.g., "1" for US)
    const nationalNumber = phoneParts[1]; // National number without country code
    return {
      countryCode: countryCallingCode,
      nationalNumber,
      // TODO: Add extension field support when available in the UI
      extension: '',
    };
  }

  return undefined;
}

/**
 * Formats a date for display as "Monday - February 3 2026"
 */
export function formatDateForDisplay(date: Date): string {
  if (!date) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const formatted = date.toLocaleDateString('en-US', options);
  return formatted.replace(', ', ' - ').replace(',', '');
}

/**
 * Formats a date for display as "Monday - February 3 2026"
 * Accepts both string and Date inputs.
 */
export function formatDate(date: string | Date): string {
  if (!date) {
    return '';
  }
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const formatted = dateObj.toLocaleDateString('en-US', options);
  return formatted.replace(', ', ' - ').replace(',', '');
}

/**
 * Formats the duration between two dates as "15 min" or "1 hr 30 min"
 */
export function formatDurationFromDates(start: Date, end: Date): string {
  if (!start || !end) {
    return '';
  }
  const diffMs = end.getTime() - start.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }
  if (minutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${minutes} min`;
}

/**
 * Formats time range with timezone.
 */
export function formatTimeRange(
  startTime: string | Date,
  endTime: string | Date,
  timezone?: string
): string {
  if (!startTime || !endTime) {
    return '';
  }

  const startDate =
    typeof startTime === 'string' ? new Date(startTime) : startTime;
  const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;
  const rawTz = timezone || getTimezone();
  const timezoneRegion = isValidTimezone(rawTz) ? rawTz : 'UTC';

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezoneRegion,
  };

  const startTimeFormatted = startDate.toLocaleTimeString('en-US', timeOptions);
  const endTimeFormatted = endDate.toLocaleTimeString('en-US', timeOptions);

  const timezoneAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneRegion,
      timeZoneName: 'short',
    })
      .formatToParts(startDate)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${startTimeFormatted} to ${endTimeFormatted}, ${
    getZoneFromTimezone(timezoneRegion, 0, startDate) || timezoneAbbr
  }`;
}

/**
 * Formats duration from string or Date inputs.
 */
export function formatDuration(
  startTime: string | Date,
  endTime: string | Date
): string {
  if (!startTime || !endTime) {
    return '';
  }

  const startDate =
    typeof startTime === 'string' ? new Date(startTime) : startTime;
  const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;

  const diffMs = endDate.getTime() - startDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }
  if (minutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${minutes} min`;
}

/**
 * Formats an array of names into a readable string.
 * Single name returns as-is, multiple names joined with commas and "and" for the last one.
 */
export function formatNames(names: string[] = []): string {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  const last = names[names.length - 1];
  const rest = names.slice(0, -1).join(', ');
  return `${rest} and ${last}`;
}

/**
 * Checks if the meeting location type indicates a video meeting.
 * Works with both MeetingLocationType and MeetingLocation enums.
 */
export function isVideoMeeting(locationType: number | undefined): boolean {
  // MeetingLocationType.VIDEO = 1
  // MeetingLocation.VIDEO_MEETING = 1
  // MeetingLocation.HYBRID_MEETING = 3
  return locationType === 1 || locationType === 3 || locationType === undefined;
}

/**
 * Checks if the meeting location type indicates an in-person meeting.
 */
export function isInPersonMeeting(
  locationType: number | MeetingLocation | undefined
): boolean {
  // MeetingLocation.IN_PERSON_MEETING = 2
  // MeetingLocation.HYBRID_MEETING = 3
  return (
    locationType === 2 ||
    locationType === 3 ||
    locationType === MeetingLocation.IN_PERSON_MEETING ||
    locationType === MeetingLocation.HYBRID_MEETING
  );
}

/**
 * Returns a human-readable text for the meeting source.
 * Handles both numeric enum values and string enum names from API.
 */
export function extractEventTypeName(title: string): string {
  const safeTitle = title || '';
  const firstDash = safeTitle.indexOf(' - ');
  return firstDash >= 0 ? safeTitle.substring(firstDash + 3) : safeTitle;
}

/**
 * Builds a display title from meeting details for batch or single meetings.
 */
export function buildDisplayTitle(
  meetingTitle: string,
  batch: boolean,
  meetings: { title?: string }[]
): string {
  if (batch) {
    return meetings.map((m) => extractEventTypeName(m.title || '')).join(', ');
  }
  return extractEventTypeName(meetingTitle);
}

/**
 * Checks if a response is a batch booking.
 */
export function isBatchBooking(response: {
  bookingGroupId?: string;
  meetings?: unknown[];
}): boolean {
  return !!response.bookingGroupId && !!response.meetings?.length;
}

/**
 * Finds the single meeting inside a batch response whose meetingId matches.
 * Returns null when meetings is empty/undefined or no meeting matches — callers
 * should fall back to the top-level response fields in that case.
 */
export function findBatchMeetingById<T extends { meetingId?: string }>(
  meetings: T[] | undefined,
  targetMeetingId: string
): T | null {
  if (!meetings?.length || !targetMeetingId) {
    return null;
  }
  return meetings.find((m) => m.meetingId === targetMeetingId) ?? null;
}

export function meetingTypeToServiceEventType(mt: MeetingType): ServiceEventType {
  return {
    id: mt.id,
    name: mt.name,
    description: mt.description,
    duration: mt.duration,
    emailRequired: mt.isEmailRequired,
    smsRequired: mt.isPhoneNumberRequired,
    notificationType: mt.notificationType,
    eventType: mt.meetingType,
    locationType: mt.locationType,
    location: mt.location,
    locationGuideline: mt.locationGuidelines,
    hostUser: mt.hostUsers || [],
    serviceForm: mt.form || undefined,
  } as ServiceEventType;
}

export function isEventUnavailable(event: ServiceEventType): boolean {
  const hosts = event.hostUser ?? [];

  if (hosts.length === 0) {
    return false;
  }

  const isMultiHost =
    event.eventType === TeamEventMeetingType.MULTI_HOST;

  return isMultiHost
    ? hosts.some(h => !h.isConnected)
    : hosts.every(h => !h.isConnected);
}

export function getMeetingSourceText(source: number | string): string {
  // MeetingSource.MEETING_SOURCE_ZOOM = 1
  // MeetingSource.MEETING_SOURCE_GOOGLE_MEET = 2
  // MeetingSource.MEETING_SOURCE_MICROSOFT = 3
  if (source === 1 || source === 'MEETING_SOURCE_ZOOM') {
    return 'Zoom';
  }
  if (source === 3 || source === 'MEETING_SOURCE_MICROSOFT') {
    return 'Microsoft Teams';
  }
  // Default to Google Meet (source === 2 or 'MEETING_SOURCE_GOOGLE_MEET')
  return 'Google Meet';
}
